<template>
  <div class="wd_container">
    <template v-if="active_state == STATES.FETCHING_WORKFLOW">
      <div class="header_skeleton">
        <v-skeleton-loader class="skeleton" type="text" />
      </div>
      <div class="tags_skeleton">
        <div v-for="value in 4" :key="value" class="tag_skeleton">
          <v-skeleton-loader class="skeleton" type="text" />
        </div>
      </div>
      <div class="main_skeleton">
        <v-skeleton-loader class="skeleton" type="text" />
      </div>
    </template>

    <template v-else-if="workflow">
      <div v-show="workflow.state === 'CLOSED'" class="banner">
        {{ $t("workflow_disabled.disabled") }}
      </div>
      <!-- HEADER SECTION -->
      <div class="header_section">
        <input
          v-if="
            [STATES.EDIT_WORKFLOW_NAME, STATES.UPDATING_WORKFLOW].includes(
              active_state
            )
          "
          ref="workflowNameInput"
          v-model="form.workflow_name"
          outlined
          type="text"
          :disabled="active_state == STATES.UPDATING_WORKFLOW"
          :placeholder="$tc('folder.workflow.newWorkflowLabel')"
          @keypress.enter="submitWorkflowName()"
        />
        <div v-else class="header_title" v-text="workflow.name" />
        <div class="actions">
          <template
            v-if="
              [STATES.EDIT_WORKFLOW_NAME, STATES.UPDATING_WORKFLOW].includes(
                active_state
              )
            "
          >
            <v-btn
              icon
              color="primary"
              :loading="active_state == STATES.UPDATING_WORKFLOW"
              @click="submitWorkflowName()"
            >
              <v-icon> $mdiCheck </v-icon>
            </v-btn>
            <v-btn
              icon
              color="secondary"
              :disabled="active_state == STATES.UPDATING_WORKFLOW"
              @click="active_state = STATES.PREVIEW"
            >
              <v-icon> $mdiClose </v-icon>
            </v-btn>
          </template>
          <v-btn v-else icon color="primary" @click="editWorkflowName()">
            <v-icon> $mdiPencilOutline </v-icon>
          </v-btn>
        </div>
      </div>
      <!-- TAG SECTION -->
      <div v-if="tags.length" class="tags">
        <div
          v-for="(tag, i) in tags"
          :key="i"
          class="tag_item"
          :class="tag.type"
        >
          <v-icon v-if="tag.icon" small class="icon" color="#ffffff">
            {{ tag.icon }}
          </v-icon>
          <span v-text="tag.label" />
        </div>
      </div>
      <!-- MAIN SECTION -->
      <div class="main_section">
        <v-tabs v-model="activeTab" class="tab_header">
          <v-tab
            v-for="(tab, i) in tabs_header"
            :key="i"
            class="tab_header_item"
          >
            <span>{{ tab.label }}</span>
            <span v-if="tab.count" class="badge">{{ tab.count }}</span>
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab">
          <!-- STATUS SECITON -->
          <v-tab-item>
            <div class="tab_item_content">
              <div class="status_container">
                <div class="status_header_section">
                  <BaseButton
                    icon="$mdiPlus"
                    @click="active_state = STATES.CREATE_STATUS"
                  >
                    {{ $t("utils.create_step") }}
                  </BaseButton>
                </div>
              </div>
              <WorkflowDetailStatus
                v-for="(status, index) in workflow.statuses"
                :key="index"
                :index-status="index + 1"
                :workflow="workflow"
                :status="status"
                :is-last-status="workflow.statuses.length - 1 === +index"
                @editStatus="editStatus($event)"
                @deleteStatus="deleteStatus($event)"
                @moveStatus="moveWorkflowStatus($event)"
              />
              <BaseAlert
                v-if="workflow.statuses.length === 0"
                color="secondary lighten-5 text-center"
              >
                <BaseParagraph bold v-text="$t('workflow.status.noStatus')" />
              </BaseAlert>
            </div>
          </v-tab-item>
          <!-- FORM_TEMPLATE SECITON -->
          <v-tab-item>
            <div class="tab_item_content">
              <FormTemplate
                :organization-id="organizationId"
                :form-templates="formTemplates"
                @createFormTemplate="active_state = STATES.CREATE_FORM_TEMPLATE"
                @editFormTemplate="editFormTemplate"
                @form-deleted="handleFormTemplateDeleted"
              />
            </div>
          </v-tab-item>
          <!-- EFFECT SECITON -->
          <v-tab-item>
            <div class="tab_item_content">
              <EffectManagement
                ref="effectManagement"
                :organization-id="organizationId"
                :workflow="workflow"
                :automation-effects="automationEffects"
                @updatedAutomationEffects="automationEffects = $event"
              />
            </div>
          </v-tab-item>
          <!-- OPERATION SECITON -->
          <v-tab-item>
            <div class="tab_item_content">
              <AutomatedOperationManagement
                ref="automated"
                :organization-id="organizationId"
                :workflow="workflow"
                :operations="operations"
                @updatedOperations="operations = $event"
              />
            </div>
          </v-tab-item>
          <!-- SETTING SECITON -->
          <v-tab-item>
            <div class="tab_item_content">
              <!-- SEND_EMAIL SECTION -->
              <div
                class="send_email_container"
                :class="{
                  disabled_email: workflow.state !== 'OPEN',
                }"
              >
                <div class="left_section">
                  <BaseIcon icon="$mdiLock" color="grey" />
                </div>
                <div class="middle_section">
                  <div v-text="$t('workflow_disabled.label')" />
                  <div
                    v-if="workflow.state == 'OPEN'"
                    class="message"
                    v-text="$t('workflow_disabled.enable_label')"
                  />
                  <div
                    v-else
                    class="message"
                    v-text="$t('workflow_disabled.disabled_label')"
                  />
                </div>
                <div class="right_section">
                  <v-btn-toggle
                    v-model="workflowState"
                    small
                    class="btn_toggle"
                    mandatory
                    @change="handleDisableWorkflow()"
                  >
                    <v-btn small :value="true">
                      <span class="btn_label" v-text="$t('utils.enabled')" />
                      <v-icon class="btn_icon" right>$mdiEye</v-icon>
                    </v-btn>

                    <v-btn small :value="false">
                      <span class="btn_label" v-text="$t('utils.disabled')" />
                      <v-icon class="btn_icon" right>$mdiEyeOff</v-icon>
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </div>
              <!-- DELETE FOLDER SECTION -->
              <div
                class="section_container"
                :class="{
                  active_section: deletionDelayDetail.active,
                }"
              >
                <div class="left_section">
                  <BaseIcon icon="$mdiFolderRemoveOutline" color="grey" />
                </div>
                <div class="middle_section">
                  <div
                    class="section_title"
                    v-text="$t('workflow_delete_folder.section.title')"
                  />
                  <div class="message" v-text="deletionDelayDetail.message" />
                </div>
                <div class="right_section">
                  <BaseButton
                    icon="$mdiCog"
                    class="h_action"
                    small
                    @click="active_state = STATES.CONFIG_DELETE_FOLDER"
                  >
                    <span v-text="$t('utils.configure')" />
                  </BaseButton>
                </div>
              </div>
              <!-- NEXUS FOLDER -->
              <WorkflowNexusConfiguration
                :organization-id="organizationId * 1"
                :workflow="workflow"
                @workflowUpdated="handleWorkflowUpdated"
              />
              <!-- INACTIVITY DAYS LIMIT -->
              <div class="inactivity_section">
                <div class="header_section">
                  <BaseTitle v-text="$t('inactive_alert.title')" />
                  <BaseButton
                    icon="$mdiPencilOutline"
                    @click="active_state = STATES.CONFIG_INACTIVITY_DAYS"
                  >
                    <span
                      v-text="
                        $t(
                          workflow.inactivityDaysLimit
                            ? 'utils.edit'
                            : 'utils.define'
                        )
                      "
                    />
                  </BaseButton>
                </div>
                <div v-if="workflow.inactivityDaysLimit" class="alert_label">
                  <span class="days" v-text="workflow.inactivityDaysLimit" />
                  <span
                    v-text="
                      $tc(
                        'inactive_alert.summary',
                        workflow.inactivityDaysLimit,
                        {
                          days: workflow.inactivityDaysLimit,
                        }
                      )
                    "
                  />
                </div>
                <div
                  v-else
                  class="alert_label"
                  v-text="$t('inactive_alert.label')"
                />
              </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </template>
    <!-- DIALOG SECTION -->
    <WorkflowCreateAndEditStatusDialog
      :dialog-state="
        [STATES.CREATE_STATUS, STATES.EDIT_STATUS].includes(active_state)
      "
      :active-status="activeStatus"
      @statusCreated="handleStatusCreated"
      @statusUpdated="handleStatusUpdated"
      @close="active_state = STATES.PREVIEW"
    />
    <WorkflowDeleteStatusDialog
      :dialog-state="active_state == STATES.DELETE_STATUS"
      :active-status="activeStatus"
      @statusDeleted="handleStatusDeleted"
      @close="active_state = STATES.PREVIEW"
    />
    <WorkflowDeleteFoldersModal
      v-if="active_state == STATES.CONFIG_DELETE_FOLDER"
      :organization-id="organizationId"
      :workflow="workflow"
      @workflowUpdated="handleWorkflowUpdated"
      @close="active_state = STATES.PREVIEW"
    />
    <WorkflowInactivityDaysLimitModal
      v-if="active_state == STATES.CONFIG_INACTIVITY_DAYS"
      :organization-id="organizationId"
      :workflow="workflow"
      @workflowUpdated="handleWorkflowUpdated"
      @close="active_state = STATES.PREVIEW"
    />
    <CosmoformTemplateCreateDialog
      v-if="
        [STATES.CREATE_FORM_TEMPLATE, STATES.EDIT_FORM_TEMPLATE].includes(
          active_state
        )
      "
      :organization-id="organizationId"
      :cosmoform-template="formTemplateSelected"
      @cosmoform-created="fetchCosmoformTemplates"
      @cosmoform-edited="handleFormTemplateEdited"
      @close="closeFormTemplate()"
    />
    <questions-create v-if="activeTab == 0" />
  </div>
</template>

<script>
import {
  fetchOrganizationWorkflow,
  patchOrganizationWorkflow,
  getOrganizationWorkflowCosmoformTemplate,
  postOrganisationWorkflowStatusMove,
} from "@/modules/Admin/Services/workflow.service";
import Workflow from "@/core/Models/Workflow";
import Status from "@/core/Models/Status";
import WorkflowAPI from "@/modules/Admin/Services/workflow.api";

import WorkflowDetailStatus from "@/modules/Admin/Components/WorkflowDetailStatus";
import WorkflowCreateAndEditStatusDialog from "@/modules/Admin/Components/WorkflowCreateAndEditStatusDialog";
import WorkflowDeleteStatusDialog from "@/modules/Admin/Components/WorkflowDeleteStatusDialog";
import FormTemplate from "@/modules/Admin/Components/CosmoformTemplate.vue";
import EffectManagement from "../Components/EffectManagement.vue";
import WorkflowDeleteFoldersModal from "../Components/WorkflowDeleteFoldersModal.vue";
import WorkflowNexusConfiguration from "../Components/WorkflowNexusConfiguration.vue";
import WorkflowInactivityDaysLimitModal from "../Components/WorkflowInactivityDaysLimitModal.vue";
import AutomatedOperationManagement from "../Components/AutomatedOperationManagement.vue";
import QuestionsCreate from "../../Folder/Components/QuestionsCreate/QuestionsCreate.vue";
import CosmoformTemplateCreateDialog from "../Components/CosmoformTemplateCreateDialog.vue";
import { CosmoformTemplate } from "@/core/Models/Cosmoform";
import { AutomationEffect } from "@/core/Models/AutomationEffect";
import { Operation } from "@/core/Models/Operation";

export default {
  name: "WorkflowDetail",
  components: {
    WorkflowDetailStatus,
    WorkflowCreateAndEditStatusDialog,
    WorkflowDeleteStatusDialog,
    FormTemplate,
    EffectManagement,
    WorkflowDeleteFoldersModal,
    WorkflowNexusConfiguration,
    WorkflowInactivityDaysLimitModal,
    AutomatedOperationManagement,
    QuestionsCreate,
    CosmoformTemplateCreateDialog,
  },
  props: {
    workflowId: {
      type: String,
      required: true,
    },
    organizationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      STATES: {
        PREVIEW: "PREVIEW",
        FETCHING_WORKFLOW: "FETCHING_WORKFLOW",
        EDIT_WORKFLOW_NAME: "EDIT_WORKFLOW_NAME",
        CREATE_STATUS: "CREATE_STATUS",
        EDIT_STATUS: "EDIT_STATUS",
        DELETE_STATUS: "DELETE_STATUS",
        CONFIG_DELETE_FOLDER: "CONFIG_DELETE_FOLDER",
        CONFIG_INACTIVITY_DAYS: "CONFIG_INACTIVITY_DAYS",
        CREATE_FORM_TEMPLATE: "CREATE_FORM_TEMPLATE",
        EDIT_FORM_TEMPLATE: "EDIT_FORM_TEMPLATE",
      },
      active_state: "FETCHING_WORKFLOW",
      workflow: null,
      workflowState: true,
      formTemplates: [],
      automationEffects: [],
      operations: [],
      form: {
        workflow_name: "",
      },
      activeTab: 0,
      activeStatus: null,
      formTemplateSelected: null,
    };
  },
  computed: {
    tabs_header() {
      if (!this.workflow) return [];
      return [
        {
          label: this.$tc(
            "workflow_detail.tabs.step",
            this.workflow.statuses.length
              ? this.workflow.statuses.length + 1
              : 1,
            {
              count: this.workflow.statuses.length,
            }
          ),
        },
        {
          label: this.$tc(
            "workflow_detail.tabs.form",
            this.formTemplates.length ? this.formTemplates.length + 1 : 1,
            {
              count: this.formTemplates.length,
            }
          ),
        },
        {
          label: this.$tc(
            "workflow_detail.tabs.action",
            this.automationEffects.length
              ? this.automationEffects.length + 1
              : 1,
            {
              count: this.automationEffects.length,
            }
          ),
        },
        {
          label: this.$tc(
            "workflow_detail.tabs.operation",
            this.operations.length ? this.operations.length + 1 : 1,
            {
              count: this.operations.length,
            }
          ),
        },
        {
          label: this.$t("workflow_detail.tabs.setting"),
        },
      ];
    },
    deletionDelayDetail() {
      if (!this.workflow) return "";
      const tmp = {
        message: this.$t("workflow_delete_folder.section.message"),
        active: false,
      };
      if (isNaN(parseInt(this.workflow.deletionDelay, 10))) return tmp;
      tmp.active = true;
      tmp.message =
        this.workflow.deletionDelay > 0
          ? this.$tc(
              "workflow_delete_folder.section.active_message",
              this.workflow.deletionDelay,
              {
                days: this.workflow.deletionDelay,
              }
            )
          : this.$t("workflow_delete_folder.section.same_day_message");
      return tmp;
    },
    tags() {
      const tags = [];
      if (!this.workflow) return tags;
      if (this.workflow.nexusFolderCreationEnabled)
        tags.push({
          label: this.$t("workflow_detail.tag_label.nexus"),
          type: "primary",
        });
      if (this.workflow.inactivityDaysLimit)
        tags.push({
          label: this.$t("workflow_detail.tag_label.alert_inativity", {
            days: this.workflow.inactivityDaysLimit,
          }),
          type: "tertiary",
        });
      return tags;
    },
  },
  watch: {},
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.active_state = this.STATES.FETCHING_WORKFLOW;
      try {
        await this.fetchWorkflow();
        await this.fetchCosmoformTemplates();
        await this.fetchWorkflowEffect();
        await this.fetchWorkflowOperations();
      } catch (error) {
        throw new Error(error);
      }
      this.active_state = this.STATES.PREVIEW;
    },
    async fetchWorkflow() {
      if (!this.organizationId || !this.workflowId) return;
      try {
        const res = await fetchOrganizationWorkflow(
          this.organizationId,
          this.workflowId
        );
        this.workflow = new Workflow(res.data);
        this.workflowState = this.workflow.state === "OPEN";
        this.setBreadcrumbLabel();
      } catch (error) {
        throw new Error(error);
      }
    },
    handleWorkflowUpdated(workflow) {
      this.workflow = new Workflow(workflow);
    },
    setBreadcrumbLabel() {
      this.$route.meta.breadcrumbs[2].label = `${this.workflow.name} `;
    },
    editWorkflowName() {
      this.active_state = this.STATES.EDIT_WORKFLOW_NAME;
      this.form.workflow_name = this.workflow.name;
      this.$nextTick(() => {
        this.$refs.workflowNameInput.focus();
      });
    },
    async submitWorkflowName() {
      await this.updateWorkflow({
        name: this.form.workflow_name,
      });
      this.setBreadcrumbLabel();
    },
    async handleDisableWorkflow() {
      try {
        const state = this.workflowState ? "OPEN" : "CLOSED";
        await patchOrganizationWorkflow(this.organizationId, this.workflowId, {
          state,
        });
        this.workflow.state = state;
        this.$store.dispatch("snackbar/active", {
          message: this.$t(
            "workflow.snackbar.success.workflowModifiedSuccessfully"
          ),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("workflow.snackbar.success.errorModifyingWorkflow"),
          type: "ERROR",
        });
      }
    },
    async updateWorkflow(payload) {
      this.active_state = this.STATES.UPDATING_WORKFLOW;
      try {
        const res = await WorkflowAPI.patchOrganizationWorkflow(
          this.organizationId,
          this.workflow.id,
          payload
        );
        this.workflow = new Workflow({
          ...res.data,
          statuses: this.workflow.statuses,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t(
            "workflow.snackbar.success.workflowModifiedSuccessfully"
          ),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.active_state = this.STATES.PREVIEW;
    },
    handleStatusCreated(status) {
      this.workflow.statuses.push(new Status(status));
      this.active_state = this.STATES.PREVIEW;
    },
    editStatus(status) {
      this.activeStatus = status;
      this.active_state = this.STATES.EDIT_STATUS;
    },
    handleStatusUpdated(status) {
      this.active_state = this.STATES.PREVIEW;
      this.activeStatus = null;
      this.workflow.statuses = this.workflow.statuses.map((s) => {
        if (s.id === status.id) return new Status(status);
        return s;
      });
    },
    deleteStatus(status) {
      this.activeStatus = status;
      this.active_state = this.STATES.DELETE_STATUS;
    },
    handleStatusDeleted(status) {
      this.active_state = this.STATES.PREVIEW;
      this.activeStatus = null;
      this.workflow.statuses = this.workflow.statuses.filter(
        (s) => s.id !== status.id
      );
    },
    async moveWorkflowStatus({ status, action }) {
      const payload = {
        organizationId: this.$route.params.organizationId,
        workflowId: this.workflow.id,
        statusId: status.id,
        action,
        status,
      };
      await postOrganisationWorkflowStatusMove(payload);
      const statutes = [...this.workflow.statuses];
      // just a simple permutation
      switch (action) {
        case "move_up": {
          const indexStatus = statutes.findIndex(
            (stat) => stat.order === status.order
          );
          statutes[indexStatus] = {
            ...statutes[indexStatus - 1],
            order: statutes[indexStatus - 1].order + 1,
          };
          statutes[indexStatus - 1] = { ...status, order: status.order - 1 };
          break;
        }
        default: {
          const indexStatus = statutes.findIndex(
            (stat) => stat.order === status.order
          );
          statutes[indexStatus] = {
            ...statutes[indexStatus + 1],
            order: statutes[indexStatus + 1].order - 1,
          };
          statutes[indexStatus + 1] = { ...status, order: status.order + 1 };
        }
      }
      this.workflow.statuses = statutes;
    },
    async fetchCosmoformTemplates() {
      try {
        const response = await getOrganizationWorkflowCosmoformTemplate({
          organizationId: this.organizationId,
          workflowId: this.$route.params.workflowId,
          filters: {},
        });
        this.formTemplates = response.item;
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    editFormTemplate(item) {
      this.formTemplateSelected = item;
      this.active_state = this.STATES.EDIT_FORM_TEMPLATE;
    },
    closeFormTemplate() {
      this.formTemplateSelected = null;
      this.active_state = this.STATES.PREVIEW;
    },
    handleFormTemplateEdited(formTemplate) {
      const index = this.formTemplates.findIndex(
        (item) => item.id == formTemplate.id
      );
      this.formTemplates.splice(index, 1, new CosmoformTemplate(formTemplate));
    },
    handleFormTemplateDeleted({ formId: templateId, needRefreshEffect }) {
      this.formTemplates = this.formTemplates.filter(
        (item) => item.id !== templateId
      );
      if (needRefreshEffect) {
        this.$refs.effectManagement.fetchWorkflowEffect();
      }
    },
    async fetchWorkflowEffect() {
      try {
        const res = await WorkflowAPI.getOrganizationWorkflowEffect({
          organizationId: this.organizationId,
          workflowId: this.workflow.id,
          params: {},
        });
        this.automationEffects = res.data.results.map(
          (d) => new AutomationEffect(d)
        );
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async fetchWorkflowOperations() {
      try {
        const res = await WorkflowAPI.getOrganizationWorkflowOperations({
          organizationId: this.organizationId,
          workflowId: this.workflow.id,
          params: {},
        });
        this.operations = res.data.results.map((d) => new Operation(d));
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.wd_container {
  position: relative;
  .banner {
    position: absolute;
    top: 0;
    left: -32px;
    width: calc(100% + 63px);
    height: 35px;
    background: #ffa7a7;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translateY(-30px);
    border-radius: 4px;
    font-size: 14px;
  }
  .header_section {
    display: flex;
    align-items: center;
    gap: 16px;
    height: 40px;
    .header_title {
      font-weight: 600;
      font-size: 24px;
      line-height: 24px;
    }
    input {
      flex: none;
      width: calc(100% - 92px);
      height: 40px;
      font-weight: 600;
      font-size: 24px;
      line-height: 24px;
      &:focus {
        outline: none;
      }
    }
    .actions {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
  .tags {
    margin-top: 8px;
    display: flex;
    gap: 16px;
    .tag_item {
      font-size: 14px;
      line-height: 18px;
      padding: 4px 16px;
      border-radius: 5px;
      color: #fff;
      background: purple;
      display: flex;
      align-items: center;
      gap: 4px;
      &.secondary {
        background: #18a300;
      }
      &.tertiary {
        background: #ff5267;
      }
      .icon {
        &::v-deep {
          color: #fff;
        }
      }
    }
  }
  .main_section {
    background: #fff;
    padding: 24px;
    margin-top: 16px;
    .tab_header {
      border-bottom: #e5e5e5 1px solid;
      .tab_header_item {
        flex: 1;
        .badge {
          height: 20px;
          padding: 4px;
          margin: 4px;
          font-size: 11px;
          list-style: 12px;
          display: flex;
          justify-content: center;
          align-items: center;
          color: #fff;
          background: #2536cc;
          border-radius: 5px;
        }
      }
    }
    .tab_item_content {
      min-height: calc(100vh - 324px);
      padding: 24px 12px;
    }
    .status_container {
      .status_header_section {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 16px;
      }
    }
  }
}
.send_email_container {
  display: flex;
  padding: 16px;
  border-radius: 5px;
  background: #e6eff4;
  gap: 16px;
  margin-bottom: 24px;
  &.disabled_email {
    background: rgba(#ff5267, 0.1);
  }
  .left_section {
    flex: none;
    width: 32px;
    height: 32px;
    border-radius: 5px;
    background: #f5f5f5;
    display: flex;
    justify-content: center;
    align-items: center;
    ::v-deep {
      .v-icon {
        width: 20px;
        height: 20px;
      }
    }
  }
  .middle_section {
    font-size: 14px;
    line-height: 20px;
    color: #353542;
    flex: 1;
    .message {
      color: #707080;
    }
  }
  .btn_toggle {
    flex: none;
    .btn_label {
      font-family: Inter;
      font-size: 12px;
      color: #353542;
    }
    .btn_icon {
      color: #353542;
      ::v-deep {
        .v-icon {
          width: 14px;
          height: 14px;
          color: #353542;
        }
      }
    }
  }
}
.section_container {
  display: flex;
  padding: 16px;
  border-radius: 5px;
  background: rgba(#ff5267, 0.1);
  gap: 16px;
  margin-bottom: 24px;
  &.active_section {
    background: #e6eff4;
  }
  .left_section {
    flex: none;
    width: 32px;
    height: 32px;
    border-radius: 5px;
    background: #f5f5f5;
    display: flex;
    justify-content: center;
    align-items: center;
    ::v-deep {
      .v-icon {
        width: 20px;
        height: 20px;
      }
    }
  }
  .middle_section {
    flex: 1;
    font-size: 14px;
    line-height: 20px;
    .section_title {
      color: #353542;
    }
    .message {
      color: #707080;
    }
  }
}
.inactivity_section {
  padding: 24px;
  background: #e6eff4;
  margin-bottom: 24px;
  .header_section {
    display: flex;
    justify-content: space-between;
  }
  .alert_label {
    color: #353542;
    font-family: Inter;
    font-size: 14px;
    font-weight: 400;
    line-height: 17px;
    display: flex;
    gap: 4px;
    align-items: center;

    .days {
      font-weight: bold;
      color: #ff5267;
      line-height: 14px;
      padding: 4px 6px;
      border-radius: 4px;
      background: rgba(#ff5267, 0.2);
    }
  }
}

.header_skeleton {
  width: 50%;
  margin-bottom: 16px;
  height: 40px;
}
.tags_skeleton {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  .tag_skeleton {
    width: 100px;
    height: 24px;
    border-radius: 5px;
  }
}
.skeleton {
  height: 100%;
  width: 100%;
  ::v-deep {
    .v-skeleton-loader__bone {
      height: 100%;
    }
  }
}
.main_skeleton {
  height: calc(100vh - 320px);
  width: 100%;
}
</style>
